import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { 
  ArrowLeft, 
  Calendar, 
  MapPin, 
  DollarSign, 
  FileText, 
  Upload, 
  Download, 
  Check, 
  X, 
  AlertTriangle,
  Building,
  User,
  Clock,
  Zap,
  // MessageSquare,
  // Paperclip
} from 'lucide-react'
import { toast } from 'react-toastify'
import apiService from '../services/api'
// import { getUserType } from '../utils/userPermissions'
import QuoteRejectionModal from '../components/QuoteRejectionModal'

// Local definitions to avoid shared package import issues
enum RejectionReason {
  TOO_EXPENSIVE = 'TOO_EXPENSIVE',
  WRONG_MOUNTING_SYSTEM = 'WRONG_MOUNTING_SYSTEM',
  MISSING_CERTIFICATIONS = 'MISSING_CERTIFICATIONS',
  DELIVERY_TOO_LONG = 'DELIVERY_TOO_LONG',
  INSUFFICIENT_WARRANTY = 'INSUFFICIENT_WARRANTY',
  TECHNICAL_SPECIFICATIONS = 'TECHNICAL_SPECIFICATIONS',
  VENDOR_REPUTATION = 'VENDOR_REPUTATION',
  PAYMENT_TERMS = 'PAYMENT_TERMS',
  LOCATION_ISSUES = 'LOCATION_ISSUES',
  OTHER = 'OTHER'
}

const REJECTION_REASON_LABELS: Record<RejectionReason, string> = {
  [RejectionReason.TOO_EXPENSIVE]: 'Too expensive',
  [RejectionReason.WRONG_MOUNTING_SYSTEM]: 'Wrong mounting system',
  [RejectionReason.MISSING_CERTIFICATIONS]: 'Missing certifications',
  [RejectionReason.DELIVERY_TOO_LONG]: 'Delivery time too long',
  [RejectionReason.INSUFFICIENT_WARRANTY]: 'Insufficient warranty terms',
  [RejectionReason.TECHNICAL_SPECIFICATIONS]: 'Technical specifications don\'t match',
  [RejectionReason.VENDOR_REPUTATION]: 'Vendor reputation concerns',
  [RejectionReason.PAYMENT_TERMS]: 'Payment terms unacceptable',
  [RejectionReason.LOCATION_ISSUES]: 'Location/shipping issues',
  [RejectionReason.OTHER]: 'Other reasons'
}

interface QuoteDetail {
  id: string
  projectType?: string
  systemSizeKw?: number
  location?: string
  budget?: number
  requestedQuantity: number
  notes?: string
  projectSpecs?: any
  status: string
  createdAt: string
  updatedAt: string
  expiresAt?: string
  buyer: {
    id: string
    firstName: string
    lastName: string
    email: string
    companyName?: string
    avatar?: string
  }
  listing?: {
    id: string
    name: string
    description: string
    vendor: {
      id: string
      firstName: string
      lastName: string
      email: string
      companyName?: string
      avatar?: string
    }
    category?: {
      name: string
    }
  }
  responses: Array<{
    id: string
    proposedTotalPrice: number
    deliveryEstimate?: string
    validUntil?: string
    message?: string
    pdfProposalUrl?: string
    status: string
    rejectionReason?: string
    rejectionComment?: string
    createdAt: string
    vendor: {
      id: string
      firstName: string
      lastName: string
      email: string
      companyName?: string
      avatar?: string
    }
  }>
}

export default function QuoteDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [quote, setQuote] = useState<QuoteDetail | null>(null)
  const [userRole, setUserRole] = useState<'buyer' | 'vendor'>('buyer')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  
  console.log('🎬 QuoteDetailPage component mounted')
  console.log('📋 id from params:', id)
  
  // PDF Upload state (for vendors)
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [uploadData, setUploadData] = useState({
    pdfFile: null as File | null,
    proposedTotalPrice: '',
    deliveryEstimate: '',
    message: ''
  })
  const [uploading, setUploading] = useState(false)
  
  // Decision state (for buyers)
  const [showDecisionModal, setShowDecisionModal] = useState(false)
  const [showRejectionModal, setShowRejectionModal] = useState(false)
  const [decisionData, setDecisionData] = useState({
    decision: '' as 'ACCEPTED' | 'REJECTED' | '',
    rejectionReason: '',
    rejectionComment: '',
    responseId: ''
  })
  const [makingDecision, setMakingDecision] = useState(false)

  useEffect(() => {
    console.log('🔄 useEffect triggered with id:', id)
    if (id) {
      console.log('✅ id exists, calling fetchQuoteDetail')
      fetchQuoteDetail()
    } else {
      console.log('❌ id is null or undefined')
    }
  }, [id])

  const fetchQuoteDetail = async () => {
    setLoading(true)
    setError(null)
    
    console.log('🔍 Fetching quote detail for ID:', id)
    
    try {
      console.log('📤 Making API call to:', `/api/enterprise/quotes/${id}`)
      const response = await apiService.get(`/api/enterprise/quotes/${id}`)
      console.log('✅ Quote detail response:', response)
      
      if ((response as any).success) {
        console.log('📋 Setting quote data:', (response as any).data.quote)
        setQuote((response as any).data.quote)
        setUserRole((response as any).data.userRole)
        console.log('✅ Quote data set successfully')
      } else {
        console.log('❌ API response not successful:', response)
        setError('Failed to load quote details')
      }
    } catch (error: any) {
      console.error('❌ Failed to fetch quote detail:', error)
      console.error('❌ Error response:', error.response)
      console.error('❌ Error message:', error.message)
      setError(error.response?.data?.message || 'Failed to load quote details')
    } finally {
      console.log('🏁 Setting loading to false')
      setLoading(false)
    }
  }

  const handlePDFUpload = async () => {
    if (!uploadData.pdfFile || !uploadData.proposedTotalPrice) {
      toast.error('Please select a PDF file and enter a proposed price')
      return
    }

    setUploading(true)
    
    try {
      // First upload the PDF file
      const formData = new FormData()
      formData.append('file', uploadData.pdfFile)
      
      const uploadResponse = await apiService.post('/api/upload/quote-documents', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      
      if (!(uploadResponse as any).success) {
        throw new Error('Failed to upload PDF file')
      }
      
      const documentUrl = (uploadResponse as any).data.documentUrl
      if (!documentUrl) {
        throw new Error('No document URL received from upload')
      }
      const pdfUrl = documentUrl
      
      // Then submit the quote response
      const response = await apiService.post(`/api/enterprise/quotes/${id}/upload-pdf`, {
        pdfUrl,
        proposedTotalPrice: uploadData.proposedTotalPrice,
        deliveryEstimate: uploadData.deliveryEstimate,
        message: uploadData.message
      }) as { success: boolean; message?: string }
      
      if (response.success) {
        toast.success('Quote PDF uploaded successfully!')
        setShowUploadModal(false)
        setUploadData({
          pdfFile: null,
          proposedTotalPrice: '',
          deliveryEstimate: '',
          message: ''
        })
        fetchQuoteDetail() // Refresh the quote
      } else {
        throw new Error(response.message || 'Failed to upload quote')
      }
    } catch (error: any) {
      console.error('Upload error:', error)
      toast.error(error.response?.data?.message || 'Failed to upload quote PDF')
    } finally {
      setUploading(false)
    }
  }

  const handleAccept = async (responseId: string) => {
    setMakingDecision(true)
    
    try {
      const response = await apiService.post(`/api/enterprise/quotes/${id}/decision`, {
        decision: 'ACCEPTED',
        responseId
      })
      
      if ((response as any).success) {
        toast.success('Quote accepted successfully!')
        fetchQuoteDetail()
      } else {
        throw new Error((response as any).message || 'Failed to accept quote')
      }
    } catch (error: any) {
      console.error('Decision error:', error)
      toast.error(error.response?.data?.message || 'Failed to accept quote')
    } finally {
      setMakingDecision(false)
    }
  }

  const handleRejectConfirm = async (reason: string, comment?: string) => {
    setMakingDecision(true)
    
    try {
      const response = await apiService.post(`/api/enterprise/quotes/${id}/decision`, {
        decision: 'REJECTED',
        rejectionReason: reason,
        rejectionComment: comment,
        responseId: decisionData.responseId
      })
      
      if ((response as any).success) {
        toast.success('Quote rejected successfully!')
        setShowRejectionModal(false)
        fetchQuoteDetail()
      } else {
        throw new Error((response as any).message || 'Failed to reject quote')
      }
    } catch (error: any) {
      console.error('Decision error:', error)
      toast.error(error.response?.data?.message || 'Failed to reject quote')
    } finally {
      setMakingDecision(false)
    }
  }

  const handleReject = (responseId: string) => {
    setDecisionData(prev => ({ ...prev, responseId }))
    setShowRejectionModal(true)
  }

  const handleDecision = async () => {
    if (!decisionData.decision) {
      toast.error('Please select accept or reject')
      return
    }
    
    if (decisionData.decision === 'REJECTED' && !decisionData.rejectionReason) {
      toast.error('Please provide a reason for rejection')
      return
    }

    setMakingDecision(true)
    
    try {
      const response = await apiService.post(`/api/enterprise/quotes/${id}/decision`, {
        decision: decisionData.decision,
        rejectionReason: decisionData.rejectionReason,
        responseId: decisionData.responseId
      })
      
      if ((response as any).success) {
        toast.success(`Quote ${decisionData.decision.toLowerCase()} successfully!`)
        setShowDecisionModal(false)
        setDecisionData({
          decision: '',
          rejectionReason: '',
          rejectionComment: '',
          responseId: ''
        })
        fetchQuoteDetail() // Refresh the quote
      } else {
        throw new Error((response as any).message || 'Failed to update quote')
      }
    } catch (error: any) {
      console.error('Decision error:', error)
      toast.error(error.response?.data?.message || 'Failed to update quote status')
    } finally {
      setMakingDecision(false)
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PENDING': return 'bg-gray-100 text-gray-800 border-gray-200'
      case 'SENT': return 'bg-blue-100 text-blue-800 border-blue-200'
      case 'RECEIVED': return 'bg-purple-100 text-purple-800 border-purple-200'
      case 'RESPONDED': return 'bg-indigo-100 text-indigo-800 border-indigo-200'
      case 'ACCEPTED': return 'bg-green-100 text-green-800 border-green-200'
      case 'REJECTED': return 'bg-red-100 text-red-800 border-red-200'
      case 'EXPIRED': return 'bg-orange-100 text-orange-800 border-orange-200'
      default: return 'bg-gray-100 text-gray-800 border-gray-200'
    }
  }

  const getStatusDescription = (status: string) => {
    switch (status) {
      case 'PENDING': return 'Quote request is being prepared'
      case 'SENT': return 'Quote request sent to vendors'
      case 'RECEIVED': return 'Quote request opened by vendor'
      case 'RESPONDED': return 'Vendor has submitted a response'
      case 'ACCEPTED': return 'Quote has been accepted'
      case 'REJECTED': return 'Quote has been rejected'
      case 'EXPIRED': return 'Quote request has expired'
      default: return 'Unknown status'
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount)
  }

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading quote details...</p>
        </div>
      </div>
    )
  }

  if (error || !quote) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center py-12">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Quote Not Found</h2>
          <p className="text-gray-600 mb-6">{error || 'The quote you are looking for does not exist or you do not have permission to view it.'}</p>
          <button
            onClick={() => navigate('/dashboard?tab=quotes')}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Quotes
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/dashboard?tab=quotes')}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Quotes
        </button>
        
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Quote Details</h1>
            <p className="text-gray-600">Quote ID: {quote.id.slice(-8)}</p>
          </div>
          
          <div className="flex items-center space-x-4">
            <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(quote.status)}`}>
              {quote.status}
            </span>
            
            {userRole === 'vendor' && ['SENT', 'RECEIVED'].includes(quote.status) && (
              <button
                onClick={() => setShowUploadModal(true)}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <Upload className="w-4 h-4 mr-2" />
                Upload Quote PDF
              </button>
            )}
            
            {userRole === 'buyer' && quote.status === 'RESPONDED' && (
              <div className="flex space-x-2">
                <button
                  onClick={() => {
                    setDecisionData({ ...decisionData, decision: 'ACCEPTED' })
                    setShowDecisionModal(true)
                  }}
                  className="inline-flex items-center px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                >
                  <Check className="w-4 h-4 mr-2" />
                  Accept Quote
                </button>
                <button
                  onClick={() => {
                    setDecisionData({ ...decisionData, decision: 'REJECTED' })
                    setShowDecisionModal(true)
                  }}
                  className="inline-flex items-center px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  <X className="w-4 h-4 mr-2" />
                  Reject Quote
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Project Details */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Project Details</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {quote.projectType && (
                <div className="flex items-center">
                  <Building className="w-5 h-5 text-gray-400 mr-3" />
                  <div>
                    <p className="text-sm text-gray-600">Project Type</p>
                    <p className="font-medium text-gray-900">{quote.projectType}</p>
                  </div>
                </div>
              )}
              
              {quote.location && (
                <div className="flex items-center">
                  <MapPin className="w-5 h-5 text-gray-400 mr-3" />
                  <div>
                    <p className="text-sm text-gray-600">Location</p>
                    <p className="font-medium text-gray-900">{quote.location}</p>
                  </div>
                </div>
              )}
              
              {quote.systemSizeKw && (
                <div className="flex items-center">
                  <Zap className="w-5 h-5 text-gray-400 mr-3" />
                  <div>
                    <p className="text-sm text-gray-600">System Size</p>
                    <p className="font-medium text-gray-900">{quote.systemSizeKw} kW</p>
                  </div>
                </div>
              )}
              
              {quote.budget && (
                <div className="flex items-center">
                  <DollarSign className="w-5 h-5 text-gray-400 mr-3" />
                  <div>
                    <p className="text-sm text-gray-600">Budget</p>
                    <p className="font-medium text-gray-900">{formatCurrency(quote.budget)}</p>
                  </div>
                </div>
              )}
              
              <div className="flex items-center">
                <FileText className="w-5 h-5 text-gray-400 mr-3" />
                <div>
                  <p className="text-sm text-gray-600">Quantity</p>
                  <p className="font-medium text-gray-900">{quote.requestedQuantity}</p>
                </div>
              </div>
              
              <div className="flex items-center">
                <Calendar className="w-5 h-5 text-gray-400 mr-3" />
                <div>
                  <p className="text-sm text-gray-600">Requested On</p>
                  <p className="font-medium text-gray-900">{formatDate(quote.createdAt)}</p>
                </div>
              </div>
            </div>
            
            {quote.notes && (
              <div className="mt-6">
                <h3 className="text-sm font-medium text-gray-900 mb-2">Additional Notes</h3>
                <p className="text-gray-700 bg-gray-50 p-3 rounded-lg">{quote.notes}</p>
              </div>
            )}
            
            {quote.projectSpecs && Object.keys(quote.projectSpecs).length > 0 && (
              <div className="mt-6">
                <h3 className="text-sm font-medium text-gray-900 mb-2">Project Specifications</h3>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <pre className="text-sm text-gray-700 whitespace-pre-wrap">
                    {JSON.stringify(quote.projectSpecs, null, 2)}
                  </pre>
                </div>
              </div>
            )}
          </div>

          {/* Quote Responses */}
          {quote.responses.length > 0 && (
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                Quote Responses ({quote.responses.length})
              </h2>
              
              <div className="space-y-4">
                {quote.responses.map((response) => (
                  <div key={response.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center mr-3">
                          {response.vendor.avatar ? (
                            <img src={response.vendor.avatar} alt="Vendor" className="w-10 h-10 rounded-full object-cover" />
                          ) : (
                            <Building className="w-5 h-5 text-gray-600" />
                          )}
                        </div>
                        <div>
                          <h3 className="font-medium text-gray-900">
                            {response.vendor.companyName || `${response.vendor.firstName} ${response.vendor.lastName}`}
                          </h3>
                          <p className="text-sm text-gray-600">{formatDate(response.createdAt)}</p>
                        </div>
                      </div>
                      
                      <div className="text-right">
                        <p className="text-lg font-bold text-green-600">
                          {formatCurrency(response.proposedTotalPrice)}
                        </p>
                        <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(response.status)}`}>
                          {response.status}
                        </span>
                      </div>
                    </div>
                    
                    {response.deliveryEstimate && (
                      <div className="mb-2">
                        <span className="text-sm text-gray-600">Delivery: </span>
                        <span className="text-sm font-medium text-gray-900">{response.deliveryEstimate}</span>
                      </div>
                    )}
                    
                    {response.message && (
                      <div className="mb-3">
                        <p className="text-sm text-gray-700">{response.message}</p>
                      </div>
                    )}
                    
                    {response.status === 'REJECTED' && response.rejectionReason && (
                      <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                        <h4 className="text-sm font-medium text-red-800 mb-1">Rejection Reason</h4>
                        <p className="text-sm text-red-700">
                          {REJECTION_REASON_LABELS[response.rejectionReason as keyof typeof REJECTION_REASON_LABELS] || response.rejectionReason}
                        </p>
                        {response.rejectionComment && (
                          <p className="text-sm text-red-600 mt-2 italic">
                            "{response.rejectionComment}"
                          </p>
                        )}
                      </div>
                    )}
                    
                    <div className="flex justify-between items-center">
                      {response.pdfProposalUrl ? (
                        <a
                          href={response.pdfProposalUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800"
                        >
                          <Download className="w-4 h-4 mr-1" />
                          Download Quote PDF
                        </a>
                      ) : (
                        <span className="text-sm text-gray-500">No PDF attached</span>
                      )}
                      
                      {userRole === 'buyer' && quote.status === 'RESPONDED' && response.status === 'PENDING' && (
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleAccept(response.id)}
                            disabled={makingDecision}
                            className="text-sm text-green-600 hover:text-green-800 font-medium disabled:opacity-50"
                          >
                            Accept
                          </button>
                          <button
                            onClick={() => handleReject(response.id)}
                            disabled={makingDecision}
                            className="text-sm text-red-600 hover:text-red-800 font-medium disabled:opacity-50"
                          >
                            Reject
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Contact Information */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              {userRole === 'buyer' ? 'Vendor Information' : 'Buyer Information'}
            </h2>
            
            {userRole === 'buyer' && quote.listing?.vendor ? (
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center mr-4">
                  {quote.listing.vendor.avatar ? (
                    <img src={quote.listing.vendor.avatar} alt="Vendor" className="w-12 h-12 rounded-full object-cover" />
                  ) : (
                    <Building className="w-6 h-6 text-gray-600" />
                  )}
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">
                    {quote.listing.vendor.companyName || `${quote.listing.vendor.firstName} ${quote.listing.vendor.lastName}`}
                  </h3>
                  <p className="text-sm text-gray-600">{quote.listing.vendor.email}</p>
                </div>
              </div>
            ) : (
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center mr-4">
                  {quote.buyer.avatar ? (
                    <img src={quote.buyer.avatar} alt="Buyer" className="w-12 h-12 rounded-full object-cover" />
                  ) : (
                    <User className="w-6 h-6 text-gray-600" />
                  )}
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">
                    {quote.buyer.companyName || `${quote.buyer.firstName} ${quote.buyer.lastName}`}
                  </h3>
                  <p className="text-sm text-gray-600">{quote.buyer.email}</p>
                </div>
              </div>
            )}
          </div>

          {/* Enhanced Timeline */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Quote Timeline</h2>
            
            <div className="space-y-4">
              {/* SENT Status */}
              <div className="flex items-center">
                <div className={`w-3 h-3 rounded-full mr-3 ${
                  ['SENT', 'RECEIVED', 'RESPONDED', 'ACCEPTED', 'REJECTED'].includes(quote.status) 
                    ? 'bg-blue-500' 
                    : 'bg-gray-300'
                }`}></div>
                <div>
                  <p className="text-sm font-medium text-gray-900">Quote Sent</p>
                  <p className="text-xs text-gray-600">{formatDate(quote.createdAt)}</p>
                </div>
              </div>
              
              {/* RECEIVED Status */}
              {['RECEIVED', 'RESPONDED', 'ACCEPTED', 'REJECTED'].includes(quote.status) && (
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-purple-500 rounded-full mr-3"></div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">Opened by Vendor</p>
                    <p className="text-xs text-gray-600">
                      {quote.status === 'RECEIVED' ? formatDate(quote.updatedAt) : 'Viewed'}
                    </p>
                  </div>
                </div>
              )}
              
              {/* RESPONDED Status */}
              {['RESPONDED', 'ACCEPTED', 'REJECTED'].includes(quote.status) && quote.responses.length > 0 && (
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-indigo-500 rounded-full mr-3"></div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">Response Submitted</p>
                    <p className="text-xs text-gray-600">{formatDate(quote.responses[0].createdAt)}</p>
                  </div>
                </div>
              )}
              
              {/* ACCEPTED Status */}
              {quote.status === 'ACCEPTED' && (
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">Quote Accepted</p>
                    <p className="text-xs text-gray-600">{formatDate(quote.updatedAt)}</p>
                  </div>
                </div>
              )}
              
              {/* REJECTED Status */}
              {quote.status === 'REJECTED' && (
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-red-500 rounded-full mr-3"></div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">Quote Rejected</p>
                    <p className="text-xs text-gray-600">{formatDate(quote.updatedAt)}</p>
                  </div>
                </div>
              )}
              
              {/* Current Status Description */}
              <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                <p className="text-sm font-medium text-gray-900">Current Status</p>
                <p className="text-xs text-gray-600 mt-1">{getStatusDescription(quote.status)}</p>
              </div>
              
              {/* Expiration Warning */}
              {quote.expiresAt && new Date(quote.expiresAt) > new Date() && (
                <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="flex items-center">
                    <Clock className="w-4 h-4 text-yellow-600 mr-2" />
                    <div>
                      <p className="text-sm font-medium text-yellow-800">Expires</p>
                      <p className="text-xs text-yellow-600">{formatDate(quote.expiresAt)}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* PDF Upload Modal (Vendors) */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Upload Quote PDF</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Quote PDF File *
                </label>
                <input
                  type="file"
                  accept=".pdf"
                  onChange={(e) => setUploadData({
                    ...uploadData,
                    pdfFile: e.target.files?.[0] || null
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Proposed Total Price *
                </label>
                <input
                  type="number"
                  value={uploadData.proposedTotalPrice}
                  onChange={(e) => setUploadData({
                    ...uploadData,
                    proposedTotalPrice: e.target.value
                  })}
                  placeholder="Enter total price"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Delivery Estimate
                </label>
                <input
                  type="text"
                  value={uploadData.deliveryEstimate}
                  onChange={(e) => setUploadData({
                    ...uploadData,
                    deliveryEstimate: e.target.value
                  })}
                  placeholder="e.g., 4-6 weeks"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Message
                </label>
                <textarea
                  value={uploadData.message}
                  onChange={(e) => setUploadData({
                    ...uploadData,
                    message: e.target.value
                  })}
                  placeholder="Additional message for the buyer..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowUploadModal(false)}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                disabled={uploading}
              >
                Cancel
              </button>
              <button
                onClick={handlePDFUpload}
                disabled={uploading || !uploadData.pdfFile || !uploadData.proposedTotalPrice}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {uploading ? 'Uploading...' : 'Upload Quote'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Decision Modal (Buyers) */}
      {showDecisionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {decisionData.decision === 'ACCEPTED' ? 'Accept Quote' : 'Reject Quote'}
            </h3>
            
            <p className="text-gray-600 mb-4">
              {decisionData.decision === 'ACCEPTED' 
                ? 'Are you sure you want to accept this quote? This action cannot be undone.'
                : 'Please provide a reason for rejecting this quote:'
              }
            </p>
            
            {decisionData.decision === 'REJECTED' && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Rejection Reason *
                </label>
                <select
                  value={decisionData.rejectionReason}
                  onChange={(e) => setDecisionData({
                    ...decisionData,
                    rejectionReason: e.target.value
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">Select a reason</option>
                  <option value="Price too high">Price too high</option>
                  <option value="Delivery time too long">Delivery time too long</option>
                  <option value="Does not meet specifications">Does not meet specifications</option>
                  <option value="Found better alternative">Found better alternative</option>
                  <option value="Project cancelled">Project cancelled</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            )}
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDecisionModal(false)}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                disabled={makingDecision}
              >
                Cancel
              </button>
              <button
                onClick={handleDecision}
                disabled={makingDecision || (decisionData.decision === 'REJECTED' && !decisionData.rejectionReason)}
                className={`px-4 py-2 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed ${
                  decisionData.decision === 'ACCEPTED' 
                    ? 'bg-green-600 hover:bg-green-700'
                    : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                {makingDecision ? 'Processing...' : `${decisionData.decision === 'ACCEPTED' ? 'Accept' : 'Reject'} Quote`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rejection Modal */}
      <QuoteRejectionModal
        isOpen={showRejectionModal}
        onClose={() => setShowRejectionModal(false)}
        onConfirm={handleRejectConfirm}
        quoteName={quote?.listing?.name || 'Quote'}
        vendorName={quote?.responses.find(r => r.id === decisionData.responseId)?.vendor.companyName || 
                   quote?.responses.find(r => r.id === decisionData.responseId)?.vendor.firstName + ' ' + 
                   quote?.responses.find(r => r.id === decisionData.responseId)?.vendor.lastName || 'Vendor'}
        loading={makingDecision}
      />
    </div>
  )
} 